import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { Header } from '@/components/landing/Header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const Legal = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Grid background — matches landing page */}
      <div
        className="absolute inset-0 w-full pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgb(100 100 100 / 0.03) 1px, transparent 1px),
            linear-gradient(to right, rgb(100 100 100 / 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          zIndex: 0,
        }}
      />

      <Header />

      <main className="flex-1 pt-[72px] relative z-10">
        <div className="container mx-auto px-6 md:px-8 py-14">
          <div className="mx-auto max-w-4xl">

            {/* Page header */}
            <div className="mb-10 space-y-2">
              <p className="text-sm text-muted-foreground uppercase tracking-widest font-medium">
                Effective Date — March 1st, 2025
              </p>
              <h1 className="text-4xl font-medium tracking-tight">Legal</h1>
              <p className="text-muted-foreground">
                Please read these documents carefully before using LindberghAI.
              </p>
            </div>

            <Tabs defaultValue="tos">
              <TabsList className="mb-8">
                <TabsTrigger value="tos">Terms of Service</TabsTrigger>
                <TabsTrigger value="privacy">Privacy Policy</TabsTrigger>
              </TabsList>

              {/* ── Terms of Service ─────────────────────────────────── */}
              <TabsContent value="tos">
                <div className="prose prose-gray max-w-none space-y-8 text-sm leading-relaxed text-muted-foreground">

                  <p>
                    Please review these Terms and Conditions (&ldquo;Terms&rdquo;) before using the LindberghAI
                    website (LindberghAI.com) and associated services (collectively, the &ldquo;Service&rdquo;)
                    operated by LindberghAI (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By accessing or using the
                    Service, you agree to be bound by these Terms.
                  </p>

                  <Section title="Acceptance of Terms">
                    By creating an account or using any of our Services, you agree to these Terms and our
                    Privacy Policy. If you use the Services on behalf of an entity, you represent that you
                    have the authority to bind the entity to these Terms.
                  </Section>

                  <Section title="User Accounts">
                    You may need to create an account to access our Service. You are responsible for
                    maintaining your account credentials&apos; confidentiality and are fully responsible for all
                    activities under your account.
                  </Section>

                  <Section title="Service Use and Restrictions">
                    The Service is not to be used for any unlawful purpose. Transmitting content that
                    infringes any third-party intellectual property rights or violates laws is prohibited.
                  </Section>

                  <Section title="Ownership of Data and Intellectual Property">
                    <SubSection label="a. Your Data and Models">
                      <ul className="list-disc pl-5 space-y-1">
                        <li>All rights to data, documents, and proprietary content you provide remain solely yours.</li>
                        <li>Submission of materials to LindberghAI does not transfer ownership of intellectual property. By submitting data, you grant LindberghAI a limited, non-exclusive, revocable license to use it solely for building, refining, and delivering the agreed-upon services, for the duration necessary to fulfill our obligations.</li>
                        <li>You are responsible for ensuring rights and permissions to use and share content with us.</li>
                        <li>LindberghAI is not liable for third-party claims related to uploaded content. We reserve the right to remove content in question if aware of potential infringements.</li>
                      </ul>
                    </SubSection>
                    <SubSection label="b. Our Processes and IP">
                      Proprietary processes, methodologies, software, algorithms, and technology used in
                      building models are the exclusive property of LindberghAI. Modifications or improvements
                      made during service provision are LindberghAI&apos;s IP. You agree not to reverse-engineer,
                      decompile, disassemble, or otherwise derive LindberghAI&apos;s proprietary technology.
                    </SubSection>
                    <SubSection label="c. Responsibility for Copyrighted Material">
                      LindberghAI provides the platform for data but does not review or guarantee the legality
                      of displayed content. By submitting copyrighted materials, you warrant that you have
                      obtained necessary rights. LindberghAI will not be held liable for copyright infringement
                      claims related to materials you provide.
                    </SubSection>
                    <SubSection label="d. User Responsibility for Submitted Materials">
                      By using LindberghAI, you affirm that you have full rights and permissions to submit any
                      materials used within our models. LindberghAI does not claim ownership over submitted
                      materials and acts solely as a hosting service. LindberghAI does not redistribute, resell,
                      or otherwise disseminate any copyrighted materials submitted by users. Users are solely
                      responsible for ensuring that any material provided complies with applicable copyright
                      laws, institutional policies, and contractual agreements. If a third party notifies us of
                      a copyright issue, we reserve the right to temporarily restrict access to the disputed
                      content until the issue is resolved.
                    </SubSection>
                    <SubSection label="e. Sharing and Redistribution of LindberghAI Tools">
                      Users may share LindberghAI tools with one another. However, redistribution, resale, or
                      public sharing of LindberghAI tools outside of authorized usage is strictly prohibited.
                      Users are responsible for ensuring that shared access complies with any applicable
                      institutional policies and agreements. LindberghAI reserves the right to revoke access if
                      unauthorized distribution is detected.
                      <br /><br />
                      If a user shares access to LindberghAI tools or links containing copyrighted materials,
                      LindberghAI assumes no liability under the Digital Millennium Copyright Act (DMCA) or any
                      other copyright law. LindberghAI functions solely as a hosting provider and does not
                      monitor, endorse, or take responsibility for shared user content. Users are entirely
                      responsible for ensuring that any shared materials comply with copyright and legal
                      requirements.
                    </SubSection>
                  </Section>

                  <Section title="Nature of the Tool">
                    The Tool is an AI-powered educational and training aid designed to supplement, not replace,
                    traditional flight instruction. It is intended to provide users with personalized feedback,
                    adaptive training, and realistic flight simulations. The Tool is not a substitute for a
                    certified flight instructor (CFI) and should not be used as such.
                  </Section>

                  <Section title="Limitation of Liability">
                    To the maximum extent permitted by applicable law, Lindbergh AI shall not be liable for any
                    direct, indirect, incidental, special, consequential, or punitive damages, including but not
                    limited to loss of profits, data, or use, arising out of or in connection with the use or
                    inability to use the Tool, even if you have been advised of the possibility of such damages.
                    This limitation of liability applies regardless of the form of action, whether in contract,
                    tort, strict liability, or otherwise.
                  </Section>

                  <Section title="User Responsibility">
                    The user is solely responsible for their use of the Tool and any decisions made based on
                    the information provided by it. The user acknowledges that flight training and aviation
                    involve inherent risks, and the use of the Tool does not eliminate these risks. The user
                    must always exercise sound judgment, adhere to all applicable aviation regulations, and
                    consult with a certified flight instructor before making any flight-related decisions.
                  </Section>

                  <Section title="Not for Real-World Flight Decisions">
                    The simulations and information provided by the Tool are for training and educational
                    purposes only and should not be used for actual flight planning or decision-making.
                    Real-world flight decisions should always be based on official sources, such as weather
                    reports, NOTAMs, and consultation with qualified aviation personnel.
                  </Section>

                  <Section title="Fees, Payment, and Refunds">
                    <SubSection label="Fees">
                      Some Service features may require a paid subscription (&ldquo;Paid Services&rdquo;). By
                      subscribing, you agree to pay associated fees, including taxes. Fees are due in advance
                      and non-refundable.
                    </SubSection>
                    <SubSection label="Payment">
                      Payments are auto-charged per your selected subscription plan. Failure to pay may result
                      in account suspension or termination.
                    </SubSection>
                    <SubSection label="No Refunds">
                      All Paid Services payments are final, with no refunds or credits for partial or unused
                      periods unless required by applicable law.
                    </SubSection>
                  </Section>

                  <Section title="Disclaimer of Warranties">
                    The Service is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; LindberghAI makes no warranties,
                    expressed or implied, including those of merchantability, fitness for a particular purpose,
                    non-infringement, or uninterrupted service.
                  </Section>

                  <Section title="Termination">
                    We reserve the right to suspend or terminate your access to the Service at any time. Upon
                    termination, your right to use the Service ceases immediately.
                  </Section>

                  <Section title="Governing Law">
                    These Terms are governed by the laws of the State of Colorado, without regard to its
                    conflict of law provisions.
                  </Section>

                  <Section title="Changes to Terms">
                    We reserve the right to modify these Terms. Notification of material changes will be posted
                    on our website or sent to your registered account. Continued use of the Service constitutes
                    acceptance of revised Terms.
                  </Section>

                  <Section title="Changes to Disclaimer">
                    LindberghAI reserves the right to modify this disclaimer at any time without notice. It is
                    the user&apos;s responsibility to review this disclaimer periodically for any changes. By using
                    the Tool, you acknowledge that you have read, understood, and agree to the terms of this
                    disclaimer.
                  </Section>

                  <Section title="Contact">
                    For questions, contact us at{' '}
                    <a href="mailto:info@LindberghAI.com" className="text-foreground underline underline-offset-2 hover:text-primary">
                      info@LindberghAI.com
                    </a>.
                  </Section>

                </div>
              </TabsContent>

              {/* ── Privacy Policy ───────────────────────────────────── */}
              <TabsContent value="privacy">
                <div className="prose prose-gray max-w-none space-y-8 text-sm leading-relaxed text-muted-foreground">

                  <p>
                    As a LindberghAI customer or a visitor to our website, we act as the &ldquo;data
                    controller&rdquo; of personal data. This means we determine how and why your data are
                    processed.
                  </p>

                  <Section title="Your Responsibilities">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Read this Privacy Policy.</li>
                      <li>
                        If you are our customer, please also check the contracts — including the Terms of
                        Service available upon request at:{' '}
                        <a href="mailto:info@LindberghAI.com" className="text-foreground underline underline-offset-2 hover:text-primary">
                          info@LindberghAI.com
                        </a>.
                      </li>
                      <li>
                        If you provide us with personal information about other people, or if others give us
                        your information, we will only use that information for the specific reason for which
                        it was provided to us. By submitting the information, you confirm that you have the
                        right to authorise us to process it on your behalf in accordance with this Privacy
                        Policy.
                      </li>
                    </ul>
                  </Section>

                  <Section title="When and How We Collect Data">
                    <p>
                      If you are only a visitor to our website, we do not collect any personal data before
                      you request a demo of LindberghAI. We use privacy-friendly analytics to carry out site
                      measurement anonymously.
                    </p>
                    <p className="mt-3">
                      From the moment you request a demo of LindberghAI, we start collecting data. Sometimes
                      you provide us with data; sometimes data about you is collected automatically.
                    </p>
                  </Section>

                  <Section title="Types of Data We Collect">
                    <div className="space-y-3">
                      {[
                        { label: 'Contact details', desc: 'Your name, email address, role in your institution, etc.' },
                        { label: 'Financial information', desc: 'Your payment information, account ID, credit/debit card details — all processed through Stripe.' },
                        { label: 'Metadata from interactions', desc: 'Function calls, JSON data passed through functions, and other requests made to our servers while using LindberghAI.' },
                        { label: 'Usage data', desc: 'We track how many times you use LindberghAI via session logs.' },
                        { label: 'Site usage data', desc: 'URL clickstreams, pages viewed, page response times, download errors, time on page, and other browsing actions on LindberghAI.com.' },
                      ].map(({ label, desc }) => (
                        <div key={label} className="flex gap-3">
                          <span className="font-semibold text-foreground shrink-0">{label}:</span>
                          <span>{desc}</span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-4">
                      We don&apos;t collect any &ldquo;sensitive data&rdquo; about you (such as racial or ethnic origin,
                      political opinions, religious beliefs, trade union membership, genetic data, biometric
                      data, health data, data about your sexual life or orientation, or offenses/alleged
                      offenses).
                    </p>
                  </Section>

                  <Section title="Sensitive Data">
                    Data classified as sensitive is processed securely using our vetted private models. We
                    implement strict de-identification processes for all data, ensuring it cannot be traced
                    back to individual users. This data undergoes regular audits to confirm FERPA compliance
                    and cannot be re-identified.
                    <br /><br />
                    LindberghAI is intended for use only by those 18 years of age or over. We do not target
                    LindberghAI at children, and we do not knowingly collect any personal data from any person
                    under 16 years of age.
                  </Section>

                  <Section title="How and Why We Use Your Data">
                    {[
                      {
                        label: 'Private Model Improvement',
                        desc: 'All data provided is securely processed within private models and is never used to train public models.',
                        basis: 'Legitimate Interests, Consent',
                      },
                      {
                        label: 'Removing Hallucinations',
                        desc: 'We continuously work to eliminate AI hallucinations. If identified or reported, we use data to promptly correct them.',
                        basis: 'Legitimate Interests, Consent',
                      },
                      {
                        label: 'User Account Management',
                        desc: 'Login and authentication, processing payments.',
                        basis: 'Legitimate Interests, Consent',
                      },
                      {
                        label: 'Improving Website Performance',
                        desc: 'Landing-page analytics, testing copy, images, marketing, and page insights.',
                        basis: 'Legitimate Interests',
                      },
                      {
                        label: 'Customer Support',
                        desc: 'Notifying you of changes to our service, solving issues via live chat, phone, or email including bug fixes.',
                        basis: 'Legitimate Interests',
                      },
                      {
                        label: 'Marketing Purposes (With Your Consent)',
                        desc: 'Sending you emails and messages about new features, products, services, and content.',
                        basis: 'Consent',
                      },
                    ].map(({ label, desc, basis }) => (
                      <div key={label} className="mt-4">
                        <p className="font-semibold text-foreground">{label}</p>
                        <p>{desc}</p>
                        <p className="text-xs mt-1"><span className="font-medium">Legal basis:</span> {basis}</p>
                      </div>
                    ))}
                  </Section>

                  <Section title="Your Privacy Choices and Rights">
                    <ul className="list-disc pl-5 space-y-2">
                      <li>You can choose not to provide us with personal data — you can still browse the website, but we will not be able to process transactions.</li>
                      <li>You can block or delete cookies through your browser settings. Some services may not function correctly if cookies are disabled.</li>
                      <li>You can ask us not to use your data for marketing by emailing <a href="mailto:info@LindberghAI.com" className="text-foreground underline underline-offset-2 hover:text-primary">info@LindberghAI.com</a>.</li>
                    </ul>
                    <p className="mt-4 font-semibold text-foreground">Your Rights</p>
                    <p>You can exercise any of the following rights by emailing us at <a href="mailto:info@LindberghAI.com" className="text-foreground underline underline-offset-2 hover:text-primary">info@LindberghAI.com</a>:</p>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                      <li>The right to access information we hold about you.</li>
                      <li>The right to have us correct any inaccurate personal data.</li>
                      <li>The right to be &ldquo;forgotten&rdquo; — to request erasure of personal data no longer necessary for our services.</li>
                      <li>The right to lodge a complaint regarding our use of your data.</li>
                    </ul>
                    <p className="mt-3">
                      We will provide information within one month of your request, unless doing so would
                      adversely affect the rights and freedoms of others.
                    </p>
                  </Section>

                  <Section title="Data Security">
                    <p>We have physical, electronic, and managerial procedures to safeguard the information we collect. We use industry-standard encryption — AES-256 for data at rest and TLS 1.2 for data in transit. We regularly review and update our security practices.</p>
                    <ul className="list-disc pl-5 space-y-1 mt-3">
                      <li>You provide personal data at your own risk — no data transmission is guaranteed to be 100% secure.</li>
                      <li>You are responsible for keeping your username and password safe and confidential.</li>
                      <li>If you believe your privacy has been breached, contact us immediately at <a href="mailto:info@LindberghAI.com" className="text-foreground underline underline-offset-2 hover:text-primary">info@LindberghAI.com</a>.</li>
                    </ul>
                  </Section>

                  <Section title="Where We Store Your Data">
                    Personal data we collect is securely stored within AWS Amazon Cloud in the U.S. All data
                    processing is conducted within the United States. By submitting your personal data, you
                    consent to the storage, processing, and transfer of this data within the United States.
                    Should we transfer your data to a third-party provider, we ensure your privacy rights are
                    protected in accordance with this Privacy Policy and U.S. data protection laws.
                  </Section>

                  <Section title="How Long We Store Your Data">
                    We will archive and stop actively using personal identifiable information within 3 months
                    from the last time you used LindberghAI. We will delete your personal data no later than
                    1 year from the last time you used LindberghAI, or as agreed in a separate contract. Data
                    deletion is managed securely to ensure all PII is fully removed after the retention period.
                    Institutions can request customised retention periods to meet specific compliance
                    requirements.
                  </Section>

                  <Section title="Third Parties Who Process Your Data">
                    We partner with trusted third-party providers to deliver our services effectively. Your
                    data is shared only when strictly required and with appropriate data protection measures in
                    place. Third-party providers such as Google Drive and M365 are evaluated to ensure they
                    can be configured to meet FERPA and GDPR compliance standards. For transfers outside the
                    U.S., we take steps to ensure compliance with applicable international data protection
                    standards.
                  </Section>

                  <Section title="Cookies">
                    You can block cookies by activating the appropriate setting in your browser. If you disable
                    cookies, certain parts of our website may not function fully. When you interact with
                    LindberghAI, we (and applicable third parties) will issue cookies unless you adjust your
                    browser settings to refuse them. These may be &lsquo;session&rsquo; cookies (deleted when you
                    leave) or &lsquo;persistent&rsquo; cookies (retained to help us recognise you on return).
                  </Section>

                  <Section title="FERPA Compliance">
                    While LindberghAI does not directly handle student records, we are committed to ensuring
                    that any educational data we process adheres to the principles of FERPA. All educational
                    data used within LindberghAI is solely for model building and course-specific improvements.
                    No student-specific information is collected, stored, or used for any other purpose. All
                    data passed through our system is anonymised or pseudonymised in accordance with privacy
                    standards.
                  </Section>

                  <Section title="Consent and Withdrawal">
                    If you have previously given consent to processing your data you can freely withdraw it at
                    any time by emailing{' '}
                    <a href="mailto:info@LindberghAI.com" className="text-foreground underline underline-offset-2 hover:text-primary">
                      info@LindberghAI.com
                    </a>.
                    {' '}If you withdraw consent and we have no other legal basis for processing, we will stop
                    processing your personal data.
                  </Section>

                  <Section title="Contact">
                    For any privacy-related questions, contact us at{' '}
                    <a href="mailto:info@LindberghAI.com" className="text-foreground underline underline-offset-2 hover:text-primary">
                      info@LindberghAI.com
                    </a>.
                  </Section>

                </div>
              </TabsContent>
            </Tabs>

          </div>
        </div>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 mt-8">
          <div className="container mx-auto px-6 md:px-8 py-8">
            <div className="flex flex-col items-center gap-5">
              <a href="/" className="flex items-center gap-2">
                <img src="/logo-icon.png" alt="LindberghAI icon" className="h-6 w-auto" />
                <span className="text-xl font-semibold tracking-tight text-foreground">
                  Lindbergh<span className="text-primary">AI</span>
                </span>
              </a>
              <div className="flex flex-nowrap items-center justify-center gap-6 text-sm text-muted-foreground">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="shrink-0 hover:text-foreground transition-colors"
                >
                  Home
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="shrink-0 hover:text-foreground transition-colors"
                >
                  Try for Free
                </button>
                <a
                  href="mailto:info@lindberghai.com"
                  className="shrink-0 hover:text-foreground transition-colors flex items-center gap-1.5"
                >
                  <Mail className="h-4 w-4" />
                  info@lindberghai.com
                </a>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

/* ── Small helper components ────────────────────────────────────── */
const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-2">
    <h2 className="text-base font-semibold text-foreground">{title}</h2>
    <div>{children}</div>
  </div>
);

const SubSection = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="mt-3 space-y-1">
    <p className="font-medium text-foreground">{label}</p>
    <div>{children}</div>
  </div>
);

export default Legal;
